import '../palette/palette.scss';

import { useLocation, useNavigate } from 'react-router';

import { isPathActive } from '@/authz/menu-visibility';
import { pageurl } from '@/constants/pageurl';
import { useHelpCapabilities } from '@/hooks/queries/help-centre/use-help-queries';

import { HC_COPY } from '../HelpCentre.Copy';
import { QuestionGlyph } from './HelpHeaderActions';

/**
 * "Help & guides" in the rail's bottom section, for everyone.
 *
 * Deliberately NOT a `menuData` entry: `roleActions` is a non-empty `ActionSet` by construction and
 * there is no "any signed-in user" code, so a menu item would have to borrow some module's gate and
 * hide the manual from everyone lacking it. The route is opened by a `ROUTE_EXTRAS` row instead
 * (`actions: null`), and this link hides itself when the server has no Help Centre.
 */
const SidebarHelpLink = ({
	onNavigate,
	bare,
}: {
	onNavigate?: () => void;
	/** Render only the `<li>`, for a host that already owns the list (the mobile menu). */
	bare?: boolean;
}) => {
	const caps = useHelpCapabilities();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	if (!caps.available) return null;

	const active = isPathActive(pageurl.HELP, pathname);
	const go = () => {
		navigate(pageurl.HELP);
		onNavigate?.();
	};

	const item = (
		<li
			className={active ? 'active' : ''}
			data-tooltip={HC_COPY.nav.help}
		>
			<div
				className='menu-header'
				role='button'
				tabIndex={0}
				onClick={go}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						go();
					}
				}}
				aria-current={active ? 'page' : undefined}
				aria-label={HC_COPY.nav.help}
			>
				<div className='svg-container'>
					<QuestionGlyph />
				</div>
				<p>{HC_COPY.nav.help}</p>
			</div>
		</li>
	);

	return bare ? item : <ul className='m-0 p-0'>{item}</ul>;
};

export default SidebarHelpLink;
